<template>
  <div class="user">
    <div class="box box-primary">
      <div class="box-header with-border">
        <h3 class="box-title">
          {{ $t('label.list.user') }}
        </h3>
        <div class="pull-right box-tools">
          <router-link
            v-if="can([$Permissions.backend.administration.user.store])"
            :to="{name: 'create-user'}"
            class="btn btn-primary btn-sm"
          >
            <i class="fa fa-plus-circle" /> {{ $t('button.newUser') }}
          </router-link>
        </div>
      </div>
      <div class="box-body">
        <div>
          <table
            id="user-table"
            class="table table-hover table-full-width table-striped table-bordered"
          >
            <thead>
              <tr>
                <th>{{ $t('table.action') }}</th>
                <th>Code</th>
                <th>{{ $t('table.id_card') }}</th>
                <th>{{ $t('label.loan_officer_access_id') }}</th>
                <th>{{ $t('string.collection_officer_value') }}</th>
                <th>{{ $t('table.email') }}</th>
                <th>{{ $t('table.gender') }}</th>
                <th>{{ $t('table.status') }}</th>
                <th>{{ $t('table.role') }}</th>
                <th>{{ $t('table.createdAt') }}</th>
              </tr>
            </thead>
            <tbody />
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'List',
  metaInfo () {
    return {
      title: this.$t('menu.userList'),
      titleTemplate: '%s | ' + process.env.VUE_APP_NAME
    }
  },
  data () {
    return {
      oTable: null
    }
  },
  watch: {
    '$i18n.locale' () {
      this.oTable.clear()
      this.oTable.destroy()
      this.oTable = this.refreshTable()
      this.oTable.draw(true)
    }
  },
  computed: {
    columns () {
      return [
        {
          data: 'action',
          name: 'action',
          width: '120px',
          orderable: false,
          searchable: false,
          title: this.$t('table.action')
        },
        { data: 'name', name: 'name', title: 'Code' },
        { data: 'id_card', name: 'id_card', title: this.$t('table.id_card') },
        {
          data: 'loan_officer_access_id',
          name: 'loan_officer_access_id',
          title: this.$t('label.loan_officer_access_id'),
          render: (data) => data || this.$t('string.na')
        },
        {
          data: 'loandisk_collection_officer',
          name: 'loandisk_collection_officer',
          title: this.$t('string.collection_officer_value'),
          render: (data) => data || this.$t('string.na')
        },
        { data: 'email', name: 'email', title: this.$t('table.email') },
        {
          data: 'gender.name_en',
          name: 'gender.name_en',
          title: this.$t('table.gender'),
          render: (data) => data || this.$t('string.na')
        },
        {
          data: 'active',
          name: 'active',
          title: this.$t('table.status'),
          searchable: false,
          render: (data, type, row) => {
            if (row.active) {
              return `<label class="label label-primary">${this.$t('string.active')}</label>`
            } else {
              return `<label class="label label-default">${this.$t('string.inactive')}</label>`
            }
          }
        },
        { data: 'role', name: 'role', title: this.$t('table.role'), searchable: true, orderable: false },
        {
          data: 'created_at',
          name: 'created_at',
          width: '150px',
          searchable: false,
          title: this.$t('table.createdAt'),
          render: (data) => {
            return this.getDateFormat(data)
          }
        }
      ]
    }
  },
  methods: {
    refreshTable () {
      const self = this
      return $('#user-table').DataTable({
        stateSave: true,
        processing: true,
        serverSide: true,
        scrollX: true,
        pageLength: 100,
        ajax: {
          method: 'POST',
          url: process.env.VUE_APP_API + '/api/v1/user/datatable',
          data: (d) => {
            d.active = self.$t('label.active')
            d.edit = self.$t('label.edit')
            d.show = self.$t('label.show')
            d.delete = self.$t('label.delete')
            d.deactive = self.$t('label.deactive')
            d.changePassword = self.$t('label.changePassword')
            d.language = self.$i18n.locale || 'en'
          },
          error: (xhr) => {
            self.onResponseError(xhr)
          }
        },
        columns: self.columns,
        language: {
          url: `/locale/${this.$i18n.locale}.json`
        },
        fixedColumns: true,
        order: [
          [9, 'desc']
        ]
      })
    },
    deleteUser (uuid) {
      const self = this
      this.$swal.fire({
        title: self.$t('label.swal.title'),
        text: self.$t('label.swal.desc'),
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        cancelButtonText: self.$t('label.swal.no'),
        confirmButtonText: self.$t('label.swal.yes')
      }).then((result) => {
        if (result.value) {
          this.$axios.post(process.env.VUE_APP_API + '/api/v1/user/delete', {
            uuid: uuid
          }).then(() => {
            this.showToastr()
            this.oTable.draw(true)
          }).catch(error => {
            this.onResponseError(error)
          })
        }
      })
    },
    toggleUserAccess (uuid) {
      this.$swal.fire({
        title: this.$t('string.are_you_sure'),
        text: this.$t('string.the_action_will_toggle_user_access'),
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        cancelButtonText: this.$t('button.cancel'),
        confirmButtonText: this.$t('string.yes')
      }).then((result) => {
        if (result.value) {
          this.$axios.post(process.env.VUE_APP_API + '/api/v1/user/toggle', {
            uuid: uuid
          }).then(() => {
            this.showToastr()
            this.oTable.draw(true)
          }).catch(error => {
            this.onResponseError(error)
          })
        }
      })
    },
    loadAction () {
      const self = this
      this.clearEventHandler([
        '.btn-delete-user',
        '.btn-toggle-user',
        '.btn-show-user',
        '.btn-edit-user',
        '.btn-reset-password-user'
      ])
      $(function () {
        $(document).on('click', '.btn-delete-user', function (e) {
          e.preventDefault()
          self.deleteUser($(this).attr('data-uuid'))
        })
        $(document).on('click', '.btn-toggle-user', function (e) {
          e.preventDefault()
          self.toggleUserAccess($(this).attr('data-uuid'))
        })
        $(document).on('click', '.btn-show-user', function (e) {
          e.preventDefault()
          self.$router.push({
            name: 'show-user',
            params: {
              uuid: $(this).attr('data-uuid')
            }
          })
        })
        $(document).on('click', '.btn-edit-user', function (e) {
          e.preventDefault()
          self.$router.push({
            name: 'edit-user',
            params: {
              uuid: $(this).attr('data-uuid')
            }
          })
        })
        $(document).on('click', '.btn-reset-password-user', function (e) {
          e.preventDefault()
          self.$router.push({
            name: 'reset-password',
            params: {
              uuid: $(this).attr('data-uuid')
            }
          })
        })
      })
    }
  },
  mounted () {
    this.oTable = this.refreshTable()
    this.loadAction()
  },
  beforeDestroy () {
    this.oTable.clear()
    this.oTable.destroy()
  }
}
</script>

<style scoped>

</style>
